import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Navbar, Container, NavDropdown } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/api';
import { getRoleLabel } from '../../constants/roles';
import { PLATFORM_BRANDING } from '../../constants/branding';
import { useToast } from '../../hooks/useToast';
import { useServerHeaderClock } from '../../hooks/useServerHeaderClock';
import { ReceptionToastStack } from '../receptionist/ui';
import { CLINICAL_WORK_SITE_SESSION_KEY } from '../nurse/nurseWorkspaceStorage';
import pharmacyService from '../../services/pharmacyService';
import Icon from '../common/Icons';
import { PHARMACY_COPY } from './pharmacyCopy';
import { PharmacyContext } from './pharmacyContext';
import '../receptionist/ReceptionistDashboard.css';
import './CentralPharmacy.css';

const SIDEBAR_KEY = 'funtoa_pharmacy_sidebar_collapsed';
const SITE_SESSION_PREFIX = 'funtoa_pharmacy_work_site';

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Shell commun pharmacie centrale / pharmacien site — aligné nurse / head-doctor.
 */
export default function PharmacyPortalShell({
  mode = 'central',
  allowedRoles = [],
  navItems = [],
  dashboardPath,
  dispensationPath,
  brandSection,
  tagline,
  sideMenuTitle
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { ACCENT } = PLATFORM_BRANDING.NAME_PARTS;
  const layoutCopy = PHARMACY_COPY.LAYOUT;
  const { toasts, showToast, removeToast, getDuration } = useToast();
  const headerClock = useServerHeaderClock(Boolean(user));

  const [workSites, setWorkSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState('');
  const [workSiteId, setWorkSiteId] = useState('');
  const [centralSite, setCentralSite] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);

  const siteSessionKey = useMemo(() => {
    const userKey = user?.id || user?.email || user?.username || 'anonymous';
    return `${SITE_SESSION_PREFIX}:${mode}:${userKey}`;
  }, [mode, user?.email, user?.id, user?.username]);

  const toast = useCallback((variant, msg) => {
    showToast(msg, variant);
  }, [showToast]);

  const bumpReload = useCallback(() => setReloadNonce((n) => n + 1), []);

  const loadWorkSites = useCallback(async () => {
    setSitesLoading(true);
    setSitesError('');
    try {
      const { sites } = await authService.getProfile();
      setWorkSites(Array.isArray(sites) ? sites : []);
    } catch (error) {
      setWorkSites([]);
      setSitesError(error.message || 'Impossible de charger vos sites de travail');
    } finally {
      setSitesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkSites();
  }, [loadWorkSites]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await pharmacyService.getCentralSite();
        if (!cancelled && res.success) setCentralSite(res.data);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (sitesLoading || workSiteId || workSites.length === 0) return;

    try {
      const storedSiteId = sessionStorage.getItem(siteSessionKey);
      if (storedSiteId && workSites.some((site) => String(site.id) === String(storedSiteId))) {
        setWorkSiteId(String(storedSiteId));
        try {
          sessionStorage.setItem(CLINICAL_WORK_SITE_SESSION_KEY, String(storedSiteId));
        } catch {
          /* ignore */
        }
        return;
      }
      sessionStorage.removeItem(siteSessionKey);
    } catch {
      /* ignore */
    }

    if (workSites.length === 1) {
      const onlySiteId = String(workSites[0].id);
      setWorkSiteId(onlySiteId);
      try {
        sessionStorage.setItem(siteSessionKey, onlySiteId);
        sessionStorage.setItem(CLINICAL_WORK_SITE_SESSION_KEY, onlySiteId);
      } catch {
        /* ignore */
      }
    }
  }, [siteSessionKey, sitesLoading, workSiteId, workSites]);

  const handleWorkSiteChange = useCallback(
    (id) => {
      setWorkSiteId(id);
      try {
        if (id) {
          sessionStorage.setItem(siteSessionKey, id);
          sessionStorage.setItem(CLINICAL_WORK_SITE_SESSION_KEY, id);
        } else {
          sessionStorage.removeItem(siteSessionKey);
          sessionStorage.removeItem(CLINICAL_WORK_SITE_SESSION_KEY);
        }
      } catch {
        /* ignore */
      }
    },
    [siteSessionKey]
  );

  const chooseWorkSite = useCallback(
    (site) => {
      handleWorkSiteChange(String(site.id));
      toast('success', layoutCopy.SITE_ACTIVE_TOAST.replace('{name}', site.name));
    },
    [handleWorkSiteChange, layoutCopy.SITE_ACTIVE_TOAST, toast]
  );

  const resetWorkSite = useCallback(() => {
    handleWorkSiteChange('');
  }, [handleWorkSiteChange]);

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem(siteSessionKey);
      sessionStorage.removeItem(CLINICAL_WORK_SITE_SESSION_KEY);
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const outletValue = useMemo(
    () => ({
      workSites,
      workSiteId,
      setWorkSiteId: handleWorkSiteChange,
      centralSite,
      toast,
      reloadNonce,
      bumpReload,
      mode
    }),
    [workSites, workSiteId, handleWorkSiteChange, centralSite, toast, reloadNonce, bumpReload, mode]
  );

  if (!user || !allowedRoles.includes(user.role)) {
    return null;
  }

  const initials =
    `${user?.last_name?.charAt(0) || ''}${user?.first_name?.charAt(0) || ''}`.toUpperCase() ||
    (user?.name || 'P').charAt(0).toUpperCase();

  const displayName =
    `${user?.last_name || ''}${user?.first_name ? ` ${user.first_name}` : ''}`.trim() ||
    user?.name ||
    layoutCopy.ACCOUNT_FALLBACK;

  const currentSite = workSites.find((s) => String(s.id) === String(workSiteId)) || null;
  const shouldShowSiteGate = !currentSite;

  const siteCardMeta = (site) => {
    const base = site.address || site.location || layoutCopy.SITE_CARD_META_FALLBACK;
    if (centralSite && String(centralSite.id) === String(site.id)) {
      return `${base}${layoutCopy.SITE_CENTRAL_SUFFIX}`;
    }
    return base;
  };

  const layoutClass = [
    'pharmacy-portal',
    'cp-layout',
    'receptionist-dashboard',
    'receptionist-layout',
    'min-vh-100',
    'd-flex',
    'flex-column',
    sidebarCollapsed ? 'cp-sidebar-is-collapsed' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={layoutClass}>
      <Navbar variant="dark" className="recv-topbar cp-topbar flex-shrink-0 recv-no-print shadow-sm">
        <Container fluid className="recv-topbar__container cp-topbar__container">
          <Navbar.Brand
            className="recv-topbar__brand-wrap cp-topbar__brand-wrap mb-0"
            onClick={() => navigate(dashboardPath)}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={PLATFORM_BRANDING.LOGO_WORDMARK_SRC}
              alt={PLATFORM_BRANDING.LOGO_WORDMARK_ALT}
              className="recv-topbar__wordmark cp-topbar__wordmark"
            />
            <span className="recv-topbar__brand cp-topbar__brand">
              <span className="recv-topbar__brand-product cp-topbar__brand-product">{ACCENT}</span>
              <span className="recv-topbar__brand-section cp-topbar__brand-section">{brandSection}</span>
            </span>
          </Navbar.Brand>

          <div className="recv-topbar__toolbar cp-topbar__toolbar ms-auto d-flex align-items-center gap-2">
            {currentSite ? (
              <>
                <span className="cp-topbar__tagline d-none d-xl-inline">{tagline}</span>
                <button
                  type="button"
                  className="recv-site-chip mx-lg-1 my-2 my-lg-0"
                  onClick={resetWorkSite}
                  title={layoutCopy.CHANGE_SITE_CHIP_TITLE}
                >
                  <span className="recv-site-chip__label">{layoutCopy.SITE_CHIP_LABEL}</span>
                  <span className="recv-site-chip__name">{currentSite.name}</span>
                </button>
              </>
            ) : workSites.length === 0 && !sitesLoading ? (
              <span className="text-white-50 small px-2">{layoutCopy.NO_SITE_ASSIGNED}</span>
            ) : null}

            <span className="recv-topbar__clock d-none d-md-inline recv-header-clock" aria-live="polite">
              {headerClock}
            </span>

            <NavDropdown
              align="end"
              className="recv-user-menu cp-user-menu"
              title={
                <span className="recv-user-menu__trigger" aria-label="Menu compte">
                  <span className="recv-user-menu__avatar" aria-hidden>
                    {initials}
                  </span>
                  <span className="recv-user-menu__meta d-none d-md-flex">
                    <span className="recv-user-menu__name">{displayName}</span>
                    <span className="recv-user-menu__role">{getRoleLabel(user?.role)}</span>
                  </span>
                  <Icon name="chevronDown" size={14} className="recv-user-menu__chevron d-none d-md-block" aria-hidden />
                </span>
              }
              id={`pharmacy-user-menu-${mode}`}
            >
              <NavDropdown.Header className="recv-user-menu__header">
                <span className="recv-user-menu__header-avatar" aria-hidden>
                  {initials}
                </span>
                <span className="recv-user-menu__header-text">
                  <span className="recv-user-menu__header-name">{displayName}</span>
                  {user?.email ? <span className="recv-user-menu__header-email">{user.email}</span> : null}
                  <span className="recv-user-menu__role-badge">{getRoleLabel(user?.role)}</span>
                </span>
              </NavDropdown.Header>
              {currentSite ? (
                <>
                  <NavDropdown.Divider className="recv-user-menu__divider" />
                  <NavDropdown.Item className="recv-user-menu__action" onClick={resetWorkSite}>
                    {layoutCopy.CHANGE_SITE_MENU}
                  </NavDropdown.Item>
                </>
              ) : null}
              <NavDropdown.Divider className="recv-user-menu__divider" />
              <NavDropdown.Item className="recv-user-menu__action text-danger" onClick={handleLogout}>
                {layoutCopy.LOGOUT}
              </NavDropdown.Item>
            </NavDropdown>
          </div>
        </Container>
      </Navbar>

      {shouldShowSiteGate ? (
        <main className="recv-site-gate flex-grow-1 overflow-auto">
          <Container fluid className="recv-site-gate__container">
            <section className="recv-site-gate__panel" aria-labelledby="pharmacy-site-gate-title">
              <div className="recv-site-gate__intro">
                <span className="recv-site-gate__badge">{layoutCopy.SITE_GATE_BADGE}</span>
                <h1 id="pharmacy-site-gate-title">{layoutCopy.SITE_GATE_TITLE}</h1>
                <p>{layoutCopy.SITE_GATE_DESC}</p>
              </div>

              {sitesLoading ? (
                <div className="recv-site-gate__state">
                  <span className="spinner-border spinner-border-sm" aria-hidden />
                  {layoutCopy.SITES_LOADING}
                </div>
              ) : sitesError ? (
                <div className="recv-site-gate__state recv-site-gate__state--error">
                  <span>{sitesError}</span>
                  <button type="button" className="cp-btn cp-btn--primary cp-btn--sm" onClick={loadWorkSites}>
                    Réessayer
                  </button>
                </div>
              ) : workSites.length === 0 ? (
                <div className="recv-site-gate__state recv-site-gate__state--error">
                  Aucun site de travail n&apos;est affecté à votre profil. Contactez un administrateur pour vous
                  attribuer au moins un site actif.
                </div>
              ) : (
                <div className="recv-site-grid" role="list">
                  {workSites.map((site) => (
                    <button
                      key={site.id}
                      type="button"
                      className="recv-site-card"
                      onClick={() => chooseWorkSite(site)}
                      role="listitem"
                    >
                      <span className="recv-site-card__icon" aria-hidden>
                        <Icon name="building" size={18} />
                      </span>
                      <span className="recv-site-card__body">
                        <span className="recv-site-card__name">{site.name}</span>
                        <span className="recv-site-card__meta">{siteCardMeta(site)}</span>
                      </span>
                      <span className="recv-site-card__cta">{layoutCopy.SITE_CARD_CTA}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </Container>
        </main>
      ) : (
        <>
          <div className="d-lg-none recv-mobile-nav cp-mobile-nav flex-shrink-0 recv-no-print">
            <Container fluid className="py-2">
              <div className="d-flex gap-2 flex-nowrap align-items-center overflow-auto">
                {navItems.map(({ to, end, label, icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={!!end}
                    className={({ isActive }) => `recv-mobile-pill cp-mobile-pill${isActive ? ' is-active' : ''}`}
                  >
                    <Icon name={icon || 'home'} size={14} className="me-1" aria-hidden />
                    {label}
                  </NavLink>
                ))}
              </div>
            </Container>
          </div>

          <div className="d-flex flex-grow-1 overflow-hidden recv-workspace">
            <aside
              className={`recv-sidebar cp-sidebar d-none d-lg-flex flex-column flex-shrink-0 recv-no-print${
                sidebarCollapsed ? ' is-collapsed' : ''
              }`}
            >
              <div className="cp-sidebar__brand">
                <span className="cp-sidebar__brand-label">FUNTOA SMIE</span>
                <span className="cp-sidebar__brand-title">{sideMenuTitle}</span>
              </div>

              <div className="cp-menu-section flex-grow-1 overflow-auto px-3 py-3">
                <div className="cp-menu-section__items">
                  {navItems.map(({ to, end, label, hint, icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={!!end}
                      title={sidebarCollapsed ? label : undefined}
                      className={({ isActive }) =>
                        `recv-menu-item cp-menu-item${isActive ? ' is-active' : ''}`
                      }
                    >
                      <span className="recv-menu-item__icon cp-menu-item__icon" aria-hidden>
                        <Icon name={icon || 'home'} size={18} />
                      </span>
                      <span className="recv-menu-item__text cp-menu-item__text">
                        <span className="recv-menu-item__label">{label}</span>
                        {hint && !sidebarCollapsed ? (
                          <span className="recv-menu-item__hint">{hint}</span>
                        ) : null}
                      </span>
                    </NavLink>
                  ))}
                </div>
              </div>

              <div className="cp-sidebar__footer px-3 pb-3 d-flex flex-column gap-2">
                {!sidebarCollapsed ? (
                  <button
                    type="button"
                    className="cp-btn cp-btn--primary w-100 cp-sidebar__cta"
                    onClick={() => navigate(dispensationPath)}
                  >
                    <Icon name="pills" size={15} aria-hidden />
                    {layoutCopy.SIDE_NEW_DISPENSATION}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="cp-btn cp-btn--primary w-100"
                    onClick={() => navigate(dispensationPath)}
                    title={layoutCopy.SIDE_NEW_DISPENSATION}
                    aria-label={layoutCopy.SIDE_NEW_DISPENSATION}
                  >
                    <Icon name="pills" size={16} aria-hidden />
                  </button>
                )}
                <button
                  type="button"
                  className="recv-btn-ghost cp-sidebar__collapse"
                  onClick={toggleSidebar}
                  aria-pressed={sidebarCollapsed}
                  title={sidebarCollapsed ? 'Développer le menu' : 'Réduire le menu'}
                >
                  <Icon name={sidebarCollapsed ? 'chevronDoubleRight' : 'chevronDoubleLeft'} size={16} aria-hidden />
                  {!sidebarCollapsed ? <span>Réduire</span> : null}
                </button>
              </div>
            </aside>

            <main className="recv-main flex-grow-1 overflow-auto">
              <Container fluid className="py-4 px-md-4 recv-dash-container cp-container">
                <PharmacyContext.Provider value={outletValue}>
                  <Outlet />
                </PharmacyContext.Provider>
              </Container>
            </main>
          </div>
        </>
      )}

      <ReceptionToastStack toasts={toasts} onRemove={removeToast} getDuration={getDuration} />
    </div>
  );
}
